/**
 * Base class for every interactive element in the theme.
 *
 * Skeleton ships no JavaScript, so this is the theme's own foundation rather
 * than a framework: a custom element base that handles the three things every
 * component here needs, and nothing else.
 *
 *   1. `refs` — child elements marked `ref="name"` are collected into
 *      `this.refs.name`. Repeat a name to get an array. Scoped so a component
 *      never reaches into a nested component's refs.
 *   2. Listeners registered with `this.on()` are removed on disconnect, which
 *      matters because the theme editor tears sections down and rebuilds them
 *      constantly.
 *   3. `connected()` / `disconnected()` hooks, so subclasses never have to
 *      remember to call super in the native callbacks.
 *
 * Subclasses declare `static requiredRefs = ['foo']` to fail loudly in the
 * editor when the markup and the behaviour drift apart.
 */
export class Component extends HTMLElement {
  /** @type {string[]} Ref names that must be present for the component to work. */
  static requiredRefs = [];

  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.refs = this.#collectRefs();

    const missing = /** @type {typeof Component} */ (this.constructor).requiredRefs.filter(
      (name) => !(name in this.refs)
    );

    if (missing.length > 0) {
      // Loud in the editor and in dev, harmless in production.
      console.warn(`<${this.localName}> is missing required ref(s): ${missing.join(', ')}`, this);
      return;
    }

    this.connected();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.disconnected();
  }

  /** Override in subclasses. Called once the element is in the document and refs resolved. */
  connected() {}

  /** Override in subclasses. Called when the element leaves the document. */
  disconnected() {}

  /**
   * Add an event listener that is torn down automatically on disconnect.
   *
   * @param {EventTarget} target
   * @param {string} type
   * @param {EventListenerOrEventListenerObject} handler
   * @param {AddEventListenerOptions} [options]
   */
  on(target, type, handler, options = {}) {
    target.addEventListener(type, handler, { ...options, signal: this.#abortController?.signal });
  }

  /**
   * An AbortSignal tied to this element's lifetime, for observers and fetches
   * that need to be cancelled on disconnect.
   */
  get signal() {
    return this.#abortController?.signal;
  }

  /**
   * Collect `ref="name"` descendants, skipping anything owned by a nested
   * component so `this.refs` only ever describes this element's own markup.
   *
   * @returns {Record<string, Element | Element[]>}
   */
  #collectRefs() {
    /** @type {Record<string, Element | Element[]>} */
    const refs = {};

    for (const element of this.querySelectorAll('[ref]')) {
      if (!this.#ownsRef(element)) continue;

      const name = element.getAttribute('ref');
      if (!name) continue;

      const existing = refs[name];
      if (existing === undefined) {
        refs[name] = element;
      } else if (Array.isArray(existing)) {
        existing.push(element);
      } else {
        refs[name] = [existing, element];
      }
    }

    return refs;
  }

  /**
   * A ref belongs to this component only if no other custom element sits
   * between it and `this` — otherwise it is the nested component's ref.
   *
   * @param {Element} element
   */
  #ownsRef(element) {
    let node = element.parentElement;

    while (node && node !== this) {
      if (node.localName.includes('-')) return false;
      node = node.parentElement;
    }

    return node === this;
  }
}

/**
 * Register a custom element, tolerating the double-definition that happens when
 * the theme editor re-injects a section's script.
 *
 * @param {string} name
 * @param {CustomElementConstructor} constructor
 */
export function define(name, constructor) {
  if (customElements.get(name)) return;
  customElements.define(name, constructor);
}
